import { useState } from 'react'
import {
  slugifyTenant,
  type TenantSummary,
  tenantIntegrations,
  useAdminTenant,
  useAdminTenants,
  useCreateTenant,
  useDeleteTenant,
  useRevealTenantCredential,
  useUpdateTenantCredentials,
} from '../../../features/admin/use-admin-tenants'
import { VENDOR_KEYS, vendorBlock } from '../../../features/admin/vendors'
import { useOrgId } from '../../use-org-id'
import { AdminPage, adminError, Meta, rowCls, VendorPanel } from './admin-bits'
import { Btn, Chip, ConfirmDialog, Dialog, Empty, Field, Inline, inputCls, Select, td, th } from './controls'
import { linkCls, mono, Panel, SLink } from './ui'

function CreateDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateTenant()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [touched, setTouched] = useState(false)
  const [banner, setBanner] = useState('')
  const effSlug = touched ? slug : slugifyTenant(name)
  const valid = name.trim() !== '' && effSlug !== ''
  const submit = () => {
    if (!valid || create.isPending) return
    create.mutate({ name: name.trim(), slug: effSlug, banner: banner || undefined }, { onSuccess: onClose })
  }
  return (
    <Dialog
      open
      title="Create tenant"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <Btn onClick={onClose} disabled={create.isPending}>
            Cancel
          </Btn>
          <Btn variant="primary" disabled={!valid || create.isPending} onClick={submit}>
            {create.isPending ? 'Creating...' : 'Create'}
          </Btn>
        </>
      }
    >
      <Field label="Name">
        <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Slug" hint="a-z 0-9 dash, unique">
        <input
          className={inputCls}
          style={mono}
          value={effSlug}
          onChange={(e) => {
            setTouched(true)
            setSlug(e.target.value)
          }}
        />
      </Field>
      <Field label="Banner">
        <Select
          label="Banner"
          value={banner}
          onChange={setBanner}
          placeholder="None"
          options={[
            ['dtlr', 'DTLR'],
            ['spc', 'SPC'],
          ]}
        />
      </Field>
      {create.error ? <Inline tone="bad">{adminError(create.error)}</Inline> : null}
    </Dialog>
  )
}

export function SignalAdminTenants() {
  const tenants = useAdminTenants()
  const del = useDeleteTenant()
  const [creating, setCreating] = useState(false)
  const [remove, setRemove] = useState<TenantSummary | null>(null)
  const list = tenants.data ?? []
  return (
    <AdminPage
      page="tenants"
      title="Tenants"
      meta={<Meta>{list.length} orgs</Meta>}
      loading={tenants.isLoading}
      error={!!tenants.error}
      right={
        <Btn variant="primary" onClick={() => setCreating(true)}>
          Create tenant
        </Btn>
      }
    >
      <Panel title="Organizations">
        {list.length === 0 ? (
          <Empty>No tenants yet.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={th}>Name</th>
                  <th className={th}>Slug</th>
                  <th className={th}>Banner</th>
                  <th className={th}>Integrations</th>
                  <th className={`${th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr key={t.id} className={rowCls}>
                    <td className={`${td} font-medium`}>
                      <SLink to={`/admin/tenants/${encodeURIComponent(t.id)}`} className={linkCls}>
                        {t.name}
                      </SLink>
                    </td>
                    <td className={td} style={mono}>
                      {t.slug}
                    </td>
                    <td className={`${td} text-[var(--color-muted)]`} style={mono}>
                      {t.banner?.toUpperCase() ?? '-'}
                    </td>
                    <td className={td}>
                      <span className="inline-flex gap-1">
                        {tenantIntegrations(t).map((i) => (
                          <Chip key={i.vendor} tone={i.enabled ? 'ok' : 'off'}>
                            {i.label}
                          </Chip>
                        ))}
                      </span>
                    </td>
                    <td className={`${td} text-right`}>
                      <Btn variant="danger" onClick={() => setRemove(t)}>
                        Delete
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {creating ? <CreateDialog onClose={() => setCreating(false)} /> : null}
      <ConfirmDialog
        open={remove !== null}
        title="Delete tenant"
        body={<>{remove?.name} and its stored credentials will be permanently removed. This cannot be undone.</>}
        confirmLabel="Delete tenant"
        danger
        pending={del.isPending}
        error={del.error ? adminError(del.error) : null}
        onConfirm={() => remove && del.mutate({ orgId: remove.id }, { onSuccess: () => setRemove(null) })}
        onClose={() => {
          del.reset()
          setRemove(null)
        }}
      />
    </AdminPage>
  )
}

export function SignalAdminTenantDetail() {
  const orgId = useOrgId()
  const tenant = useAdminTenant(orgId)
  const update = useUpdateTenantCredentials()
  const reveal = useRevealTenantCredential()
  return (
    <AdminPage
      page={orgId}
      parent={{ label: 'tenants', to: '/admin/tenants' }}
      title="Tenant credentials"
      loading={tenant.isLoading}
      error={!!tenant.error}
    >
      {VENDOR_KEYS.map((v) => (
        <VendorPanel
          key={v}
          vendor={v}
          block={vendorBlock(tenant.data, v)}
          pending={update.isPending && update.variables?.vendor === v}
          error={update.variables?.vendor === v ? update.error : null}
          onSave={(body) => update.mutate({ orgId, ...body })}
          reveal={(vendor, field) => reveal.mutateAsync({ orgId, vendor, field })}
        />
      ))}
    </AdminPage>
  )
}
